/**
 * Bodies — a small sequential-impulse rigid body world for debris, shell
 * casings, dropped weapons and gibs.
 *
 * Shapes: 'box' (OBB) and 'sphere'.
 * Static geometry: the TriBVH (contacts generated from box corners / sphere
 * centre against triangles, using the true closest-point so edges and vertices
 * behave).
 * Dynamic pairs: sweep-and-prune broadphase on x, then OBB corner narrowphase.
 * Solver: sequential impulses with Baumgarte + speculative contacts, restitution,
 * Coulomb friction, and a sleep threshold so settled piles cost nothing.
 *
 * Instanced rendering: bodies created without a `mesh` get a slot in
 * `world.debrisMesh` (a THREE.InstancedMesh of unit boxes) or
 * `world.debrisSphereMesh`. The FX team can read these directly — see the
 * `debrisMesh` note in Physics.js.
 *
 * owner: physics
 */
import * as THREE from 'three';
import { makeRng } from '../core/Rand.js';
import { closestPointOnTriangle, closestPointOnOBB, clamp } from './PhysMath.js';

const SLOP = 0.0015;
const BAUMGARTE = 0.22;
const MAX_CORRECTION = 3.0;      // m/s of positional correction velocity
const SPECULATIVE = 0.03;        // contact generation margin
const MAX_PENETRATION = 0.08;    // deepest corner overlap we will try to resolve
const REST_THRESHOLD = 1.0;      // m/s below which restitution is dropped
const SLEEP_LIN = 0.055;
const SLEEP_ANG = 0.22;
const SLEEP_TIME = 0.55;
// Coulomb-style rolling / sliding resistance applied to bodies that are in
// contact. Without it a sphere on a flat floor rolls forever and debris never
// sleeps — real engines all carry an equivalent term.
const ROLL_DECEL = 3.2;          // rad/s²
const DRIFT_DECEL = 0.40;        // m/s²

let nextBodyId = 1;

export class Body {
  constructor(opts = {}) {
    this.id = nextBodyId++;
    this.shape = opts.shape === 'sphere' ? 'sphere' : 'box';
    this.half = new THREE.Vector3(0.1, 0.1, 0.1);
    if (opts.size) {
      if (typeof opts.size === 'number') this.half.setScalar(opts.size * 0.5);
      else this.half.set(opts.size.x * 0.5, opts.size.y * 0.5, opts.size.z * 0.5);
    }
    this.radius = opts.radius ?? (this.shape === 'sphere' ? this.half.x : Math.min(this.half.x, this.half.y, this.half.z));
    if (this.shape === 'sphere') this.half.setScalar(this.radius);

    this.mass = opts.mass ?? 1;
    this.invMass = this.mass > 0 ? 1 / this.mass : 0;
    this.position = new THREE.Vector3().copy(opts.position || new THREE.Vector3());
    this.quaternion = new THREE.Quaternion().copy(opts.quaternion || _identQ);
    this.velocity = new THREE.Vector3().copy(opts.velocity || _zero);
    this.angularVelocity = new THREE.Vector3().copy(opts.angularVelocity || _zero);

    this.restitution = opts.restitution ?? 0.18;
    this.friction = opts.friction ?? 0.62;
    this.linearDamping = opts.linearDamping ?? 0.02;
    this.angularDamping = opts.angularDamping ?? 0.06;
    this.gravityScale = opts.gravityScale ?? 1;

    this.mesh = opts.mesh || null;
    this.instance = -1;
    this.instanceKind = null;
    this.mask = opts.mask ?? 8;         // MASK.DEBRIS
    this.collidesWith = opts.collidesWith ?? 0xffff;
    this.userData = opts.userData || {};
    this.onSleep = opts.onSleep || null;
    this.lifetime = opts.lifetime ?? Infinity;
    this.age = 0;

    this.sleeping = false;
    this.sleepTimer = 0;
    this.removed = false;
    this.touchCount = 0;
    this.wasMoving = true;   // post-solve motion flag; drives the wake cascade

    // split-impulse (pseudo) velocities: positional error is resolved here so
    // the Baumgarte term never pumps energy into the real velocity.
    this.pseudoVel = new THREE.Vector3();
    this.pseudoAng = new THREE.Vector3();

    // local inertia tensor (diagonal)
    this.invInertia = new THREE.Vector3();
    this._computeInertia();

    this.aabbMin = new THREE.Vector3();
    this.aabbMax = new THREE.Vector3();
    this._matrix = new THREE.Matrix4();
    this.updateDerived();
  }

  _computeInertia() {
    if (this.invMass === 0) { this.invInertia.set(0, 0, 0); return; }
    const m = this.mass;
    if (this.shape === 'sphere') {
      const i = 0.4 * m * this.radius * this.radius;
      this.invInertia.set(1 / i, 1 / i, 1 / i);
    } else {
      const { x: hx, y: hy, z: hz } = this.half;
      const ix = (m / 3) * (hy * hy + hz * hz);
      const iy = (m / 3) * (hx * hx + hz * hz);
      const iz = (m / 3) * (hx * hx + hy * hy);
      this.invInertia.set(1 / Math.max(ix, 1e-9), 1 / Math.max(iy, 1e-9), 1 / Math.max(iz, 1e-9));
    }
  }

  updateDerived() {
    this._matrix.compose(this.position, this.quaternion, _one);
    if (this.shape === 'sphere') {
      this.aabbMin.set(this.position.x - this.radius, this.position.y - this.radius, this.position.z - this.radius);
      this.aabbMax.set(this.position.x + this.radius, this.position.y + this.radius, this.position.z + this.radius);
    } else {
      const e = this._matrix.elements;
      const ex = Math.abs(e[0]) * this.half.x + Math.abs(e[4]) * this.half.y + Math.abs(e[8]) * this.half.z;
      const ey = Math.abs(e[1]) * this.half.x + Math.abs(e[5]) * this.half.y + Math.abs(e[9]) * this.half.z;
      const ez = Math.abs(e[2]) * this.half.x + Math.abs(e[6]) * this.half.y + Math.abs(e[10]) * this.half.z;
      this.aabbMin.set(this.position.x - ex, this.position.y - ey, this.position.z - ez);
      this.aabbMax.set(this.position.x + ex, this.position.y + ey, this.position.z + ez);
    }
  }

  wake() {
    this.sleeping = false;
    this.sleepTimer = 0;
  }

  /** Apply an impulse (N·s) at a world point. */
  applyImpulse(ix, iy, iz, px, py, pz) {
    if (this.invMass === 0) return;
    this.wake();
    this.velocity.x += ix * this.invMass;
    this.velocity.y += iy * this.invMass;
    this.velocity.z += iz * this.invMass;
    if (px !== undefined) {
      const rx = px - this.position.x, ry = py - this.position.y, rz = pz - this.position.z;
      const tx = ry * iz - rz * iy, ty = rz * ix - rx * iz, tz = rx * iy - ry * ix;
      applyInvInertia(this, tx, ty, tz, _tmpV);
      this.angularVelocity.add(_tmpV);
    }
  }

  get maxHalf() { return Math.max(this.half.x, this.half.y, this.half.z); }
  get minHalf() { return Math.min(this.half.x, this.half.y, this.half.z); }
}

/* ------------------------------------------------------------------ */

class Contact {
  constructor() {
    this.a = null; this.b = null;
    this.px = 0; this.py = 0; this.pz = 0;
    this.nx = 0; this.ny = 0; this.nz = 0;   // points from B (or static) toward A
    this.separation = 0;
    this.restitution = 0; this.friction = 0;
    this.normalImpulse = 0; this.tangentImpulse1 = 0; this.tangentImpulse2 = 0;
    this.rax = 0; this.ray = 0; this.raz = 0;
    this.rbx = 0; this.rby = 0; this.rbz = 0;
    this.normalMass = 0; this.tMass1 = 0; this.tMass2 = 0;
    this.t1x = 0; this.t1y = 0; this.t1z = 0;
    this.t2x = 0; this.t2y = 0; this.t2z = 0;
    this.velocityBias = 0;
    this.positionBias = 0;
    this.pseudoImpulse = 0;
    this.surface = 0;
  }
}

export class BodyWorld {
  constructor(queries, opts = {}) {
    this.queries = queries;
    this.gravity = opts.gravity ?? -(9.81 * 2.2);
    this.bodies = [];
    this.contacts = [];
    this.contactCount = 0;
    this.iterations = opts.iterations ?? 8;
    this.rng = makeRng(opts.seed ?? 0xdeb815);
    this.stats = { bodies: 0, awake: 0, contacts: 0, ms: 0, pairs: 0 };

    this._sapOrder = [];
    this._pairs = [];
    this._staticMask = opts.staticMask ?? 1;

    this.debrisMesh = null;
    this.debrisSphereMesh = null;
    this._freeBox = [];
    this._freeSphere = [];
    this._capacity = opts.instanceCapacity ?? 512;
  }

  /**
   * Build the instanced debris meshes. Exposed to the FX team as
   * `ctx.physics.debrisMesh` / `ctx.physics.debrisSphereMesh`.
   */
  createInstancedMeshes(scene, boxMaterial, sphereMaterial) {
    const cap = this._capacity;
    const boxGeo = new THREE.BoxGeometry(1, 1, 1);
    const sphGeo = new THREE.IcosahedronGeometry(0.5, 1);
    this.debrisMesh = new THREE.InstancedMesh(boxGeo, boxMaterial, cap);
    this.debrisMesh.name = 'physics-debris-boxes';
    this.debrisMesh.frustumCulled = false;
    this.debrisMesh.castShadow = true;
    this.debrisMesh.receiveShadow = true;
    this.debrisMesh.userData.noCollide = true;
    this.debrisMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.debrisSphereMesh = new THREE.InstancedMesh(sphGeo, sphereMaterial, cap);
    this.debrisSphereMesh.name = 'physics-debris-spheres';
    this.debrisSphereMesh.frustumCulled = false;
    this.debrisSphereMesh.castShadow = true;
    this.debrisSphereMesh.receiveShadow = true;
    this.debrisSphereMesh.userData.noCollide = true;
    this.debrisSphereMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    for (let i = cap - 1; i >= 0; i--) { this._freeBox.push(i); this._freeSphere.push(i); }
    _hideAll(this.debrisMesh, cap);
    _hideAll(this.debrisSphereMesh, cap);
    scene.add(this.debrisMesh);
    scene.add(this.debrisSphereMesh);
    return this.debrisMesh;
  }

  add(opts) {
    const b = new Body(opts);
    if (!b.mesh && this.debrisMesh) {
      const pool = b.shape === 'sphere' ? this._freeSphere : this._freeBox;
      if (pool.length) {
        b.instance = pool.pop();
        b.instanceKind = b.shape === 'sphere' ? 'sphere' : 'box';
      }
    }
    this.bodies.push(b);
    this._sapOrder.push(this.bodies.length - 1);
    this.syncTransform(b);
    return b;
  }

  remove(body) {
    const i = this.bodies.indexOf(body);
    if (i < 0) return false;
    body.removed = true;
    if (body.instance >= 0) {
      const mesh = body.instanceKind === 'sphere' ? this.debrisSphereMesh : this.debrisMesh;
      _hideInstance(mesh, body.instance);
      (body.instanceKind === 'sphere' ? this._freeSphere : this._freeBox).push(body.instance);
      body.instance = -1;
    }
    this.bodies.splice(i, 1);
    this._sapOrder.length = this.bodies.length;
    for (let k = 0; k < this.bodies.length; k++) this._sapOrder[k] = k;
    return true;
  }

  clear() {
    for (const b of this.bodies.slice()) this.remove(b);
  }

  /* ------------------------------------------------------------------ *
   * Simulation step
   * ------------------------------------------------------------------ */
  step(dt) {
    const t0 = _now();
    const bodies = this.bodies;
    const n = bodies.length;
    if (n === 0) { this.stats.bodies = 0; this.stats.awake = 0; this.stats.contacts = 0; this.stats.ms = 0; return; }

    // 1. integrate velocities
    let awake = 0;
    for (let i = 0; i < n; i++) {
      const b = bodies[i];
      b.age += dt;
      if (b.sleeping || b.invMass === 0) continue;
      awake++;
      b.velocity.y += this.gravity * b.gravityScale * dt;
      const ld = Math.max(0, 1 - b.linearDamping * dt);
      const ad = Math.max(0, 1 - b.angularDamping * dt);
      b.velocity.multiplyScalar(ld);
      b.angularVelocity.multiplyScalar(ad);
    }

    // 2. contacts
    this.contactCount = 0;
    for (let i = 0; i < n; i++) bodies[i].touchCount = 0;
    for (let i = 0; i < n; i++) {
      const b = bodies[i];
      if (b.sleeping || b.invMass === 0) continue;
      this._staticContacts(b, dt);
    }
    this._dynamicContacts(dt);

    // 3. sequential impulses — real velocity pass, then a split-impulse
    //    (pseudo velocity) pass that removes penetration without adding energy
    this._prepare(dt);
    for (let it = 0; it < this.iterations; it++) this._solveVelocity();
    for (let it = 0; it < 3; it++) this._solvePosition();

    // 4. integrate positions (with a cheap CCD clamp against static geometry)
    for (let i = 0; i < n; i++) {
      const b = bodies[i];
      if (b.sleeping || b.invMass === 0) continue;
      _step.copy(b.velocity).add(b.pseudoVel).multiplyScalar(dt);
      const move = _step.length();
      const limit = Math.max(0.02, b.minHalf * 0.9);
      if (move > limit && this.queries.bvh.triCount > 0) {
        // Continuous clamp: never let a body pass through a wall in one step.
        const r = Math.max(0.008, b.minHalf * 0.5);
        if (this.queries.sweepCapsule(b.position, b.position, r, _step, this._staticMask, _ccd)) {
          const travel = Math.max(0, _ccd.t * move - 0.004);
          b.position.addScaledVector(_step, travel / move);
          const vn = b.velocity.x * _ccd.nx + b.velocity.y * _ccd.ny + b.velocity.z * _ccd.nz;
          if (vn < 0) {
            b.velocity.x -= _ccd.nx * vn * (1 + b.restitution);
            b.velocity.y -= _ccd.ny * vn * (1 + b.restitution);
            b.velocity.z -= _ccd.nz * vn * (1 + b.restitution);
          }
        } else b.position.add(_step);
      } else {
        b.position.add(_step);
      }

      _tmpV.copy(b.angularVelocity).add(b.pseudoAng);
      const wl = _tmpV.length();
      if (wl > 1e-7) {
        _tmpQ.setFromAxisAngle(_tmpV.multiplyScalar(1 / wl), wl * dt);
        b.quaternion.premultiply(_tmpQ).normalize();
      }
      b.pseudoVel.set(0, 0, 0);
      b.pseudoAng.set(0, 0, 0);
      b.updateDerived();
    }

    // 6. rolling / sliding resistance + sleeping
    for (let i = 0; i < n; i++) {
      const b = bodies[i];
      if (b.invMass === 0 || b.sleeping) continue;
      if (b.touchCount > 0) {
        const wl = b.angularVelocity.length();
        if (wl > 1e-9) {
          const dec = Math.min(wl, ROLL_DECEL * dt);
          b.angularVelocity.multiplyScalar((wl - dec) / wl);
        }
        const vl = Math.hypot(b.velocity.x, b.velocity.z);
        if (vl > 1e-9) {
          const dv = Math.min(vl, DRIFT_DECEL * dt);
          const s = (vl - dv) / vl;
          b.velocity.x *= s; b.velocity.z *= s;
        }
      }
      const lin = b.velocity.lengthSq();
      const ang = b.angularVelocity.lengthSq();
      b.wasMoving = lin >= SLEEP_LIN * SLEEP_LIN || ang >= SLEEP_ANG * SLEEP_ANG;
      if (!b.wasMoving) {
        b.sleepTimer += dt;
        if (b.sleepTimer >= SLEEP_TIME) {
          b.sleeping = true;
          b.velocity.set(0, 0, 0);
          b.angularVelocity.set(0, 0, 0);
          if (b.onSleep) b.onSleep(b);
        }
      } else b.sleepTimer = 0;
    }

    this.stats.bodies = n;
    this.stats.awake = awake;
    this.stats.contacts = this.contactCount;
    this.stats.ms = _now() - t0;
  }

  /* ---- contact generation vs the static BVH ---- */
  _staticContacts(b, dt) {
    const q = this.queries;
    const bvh = q.bvh;
    if (bvh.triCount === 0) return;
    const pad = SPECULATIVE + 0.002;
    const cnt = q._gather(
      b.aabbMin.x - pad, b.aabbMin.y - pad, b.aabbMin.z - pad,
      b.aabbMax.x + pad, b.aabbMax.y + pad, b.aabbMax.z + pad,
      this._staticMask & b.collidesWith,
    );
    if (cnt === 0) return;
    const cand = q._cand, P = bvh.pos;

    if (b.shape === 'sphere') {
      for (let i = 0; i < cnt; i++) {
        const tri = cand[i], o = tri * 9;
        const d2 = closestPointOnTriangle(
          b.position.x, b.position.y, b.position.z,
          P[o], P[o + 1], P[o + 2], P[o + 3], P[o + 4], P[o + 5], P[o + 6], P[o + 7], P[o + 8], _cp,
        );
        const d = Math.sqrt(d2);
        const sep = d - b.radius;
        if (sep > SPECULATIVE) continue;
        let nx, ny, nz;
        if (d > 1e-6) { nx = (b.position.x - _cp.x) / d; ny = (b.position.y - _cp.y) / d; nz = (b.position.z - _cp.z) / d; }
        else { const n3 = tri * 3; nx = bvh.nrm[n3]; ny = bvh.nrm[n3 + 1]; nz = bvh.nrm[n3 + 2]; }
        this._pushContact(b, null, _cp.x, _cp.y, _cp.z, nx, ny, nz, sep, bvh.surf[tri]);
      }
      return;
    }

    // box: the 8 corners are the contact features. This gives 4 contacts for a
    // box resting flat on a floor, which is what makes the pile stable.
    const m = b._matrix.elements;
    const hx = b.half.x, hy = b.half.y, hz = b.half.z;
    for (let c = 0; c < 8; c++) {
      const sx = (c & 1) ? hx : -hx;
      const sy = (c & 2) ? hy : -hy;
      const sz = (c & 4) ? hz : -hz;
      const wx = b.position.x + m[0] * sx + m[4] * sy + m[8] * sz;
      const wy = b.position.y + m[1] * sx + m[5] * sy + m[9] * sz;
      const wz = b.position.z + m[2] * sx + m[6] * sy + m[10] * sz;

      let bestSep = Infinity, bnx = 0, bny = 0, bnz = 0, bpx = 0, bpy = 0, bpz = 0, bsurf = 0, found = false;
      for (let i = 0; i < cnt; i++) {
        const tri = cand[i], o = tri * 9;
        const n3 = tri * 3;
        const fnx = bvh.nrm[n3], fny = bvh.nrm[n3 + 1], fnz = bvh.nrm[n3 + 2];
        const d2 = closestPointOnTriangle(
          wx, wy, wz,
          P[o], P[o + 1], P[o + 2], P[o + 3], P[o + 4], P[o + 5], P[o + 6], P[o + 7], P[o + 8], _cp,
        );
        const d = Math.sqrt(d2);
        // The true distance to the triangle is the only safe depth measure. A
        // signed-plane test alone would make the corner of a box standing ON a
        // slab collide with the slab's far face, which flings bodies away.
        const signed = (wx - P[o]) * fnx + (wy - P[o + 1]) * fny + (wz - P[o + 2]) * fnz;
        let sep, nx, ny, nz;
        if (signed < 0) {
          const depth = -signed;
          // Reject a triangle the corner does not project onto: otherwise a
          // 0.3 mm overlap of a tessellated floor reports the distance to the
          // *neighbouring* triangle's diagonal edge as an 18 mm penetration.
          if (depth > MAX_PENETRATION || d > depth + 1e-3) continue;
          sep = signed;                  // true perpendicular depth
          nx = fnx; ny = fny; nz = fnz;
        } else {
          if (d > SPECULATIVE) continue;
          sep = d;
          if (d > 1e-6) { nx = (wx - _cp.x) / d; ny = (wy - _cp.y) / d; nz = (wz - _cp.z) / d; }
          else { nx = fnx; ny = fny; nz = fnz; }
        }
        if (sep < bestSep) {
          bestSep = sep; bnx = nx; bny = ny; bnz = nz;
          bpx = _cp.x; bpy = _cp.y; bpz = _cp.z; bsurf = bvh.surf[tri]; found = true;
        }
      }
      if (found) this._pushContact(b, null, bpx, bpy, bpz, bnx, bny, bnz, bestSep, bsurf);
    }
  }

  /* ---- sweep-and-prune broadphase + dynamic narrowphase ---- */
  _dynamicContacts() {
    const bodies = this.bodies;
    const n = bodies.length;
    const order = this._sapOrder;
    if (order.length !== n) { order.length = n; for (let i = 0; i < n; i++) order[i] = i; }
    // insertion sort on x-min — the list is nearly sorted frame to frame
    for (let i = 1; i < n; i++) {
      const v = order[i];
      const key = bodies[v].aabbMin.x;
      let j = i - 1;
      while (j >= 0 && bodies[order[j]].aabbMin.x > key) { order[j + 1] = order[j]; j--; }
      order[j + 1] = v;
    }
    let pairs = 0;
    for (let i = 0; i < n; i++) {
      const a = bodies[order[i]];
      for (let j = i + 1; j < n; j++) {
        const b = bodies[order[j]];
        if (b.aabbMin.x > a.aabbMax.x) break;
        if (a.sleeping && b.sleeping) continue;
        if ((a.mask & b.collidesWith) === 0 && (b.mask & a.collidesWith) === 0) continue;
        if (a.aabbMin.y > b.aabbMax.y || a.aabbMax.y < b.aabbMin.y) continue;
        if (a.aabbMin.z > b.aabbMax.z || a.aabbMax.z < b.aabbMin.z) continue;
        pairs++;
        this._narrow(a, b);
      }
    }
    this.stats.pairs = pairs;
  }

  _narrow(a, b) {
    if (a.shape === 'sphere' && b.shape === 'sphere') {
      _tmpV.subVectors(a.position, b.position);
      const d = _tmpV.length();
      const sep = d - (a.radius + b.radius);
      if (sep > SPECULATIVE) return;
      if (d > 1e-6) _tmpV.multiplyScalar(1 / d); else _tmpV.set(0, 1, 0);
      const px = b.position.x + _tmpV.x * b.radius;
      const py = b.position.y + _tmpV.y * b.radius;
      const pz = b.position.z + _tmpV.z * b.radius;
      this._pushContact(a, b, px, py, pz, _tmpV.x, _tmpV.y, _tmpV.z, sep, 0);
      return;
    }
    if (a.shape === 'sphere') { this._sphereBox(a, b, 1); return; }
    if (b.shape === 'sphere') { this._sphereBox(b, a, -1); return; }
    // box vs box: corners of each inside the other
    this._boxCorners(a, b);
    this._boxCorners(b, a, true);
  }

  _sphereBox(sph, box, sign) {
    closestPointOnOBB(
      sph.position.x, sph.position.y, sph.position.z,
      box.position.x, box.position.y, box.position.z,
      box._matrix.elements, box.half.x, box.half.y, box.half.z, _cp,
    );
    _tmpV.set(sph.position.x - _cp.x, sph.position.y - _cp.y, sph.position.z - _cp.z);
    const d = _tmpV.length();
    const sep = d - sph.radius;
    if (sep > SPECULATIVE) return;
    if (d > 1e-6) _tmpV.multiplyScalar(1 / d);
    else _tmpV.subVectors(sph.position, box.position).normalize();
    // normal must point from B toward A
    if (sign > 0) this._pushContact(sph, box, _cp.x, _cp.y, _cp.z, _tmpV.x, _tmpV.y, _tmpV.z, sep, 0);
    else this._pushContact(box, sph, _cp.x, _cp.y, _cp.z, -_tmpV.x, -_tmpV.y, -_tmpV.z, sep, 0);
  }

  /** Contacts from A's corners penetrating B's OBB. */
  _boxCorners(a, b, flip = false) {
    const m = a._matrix.elements;
    const hx = a.half.x, hy = a.half.y, hz = a.half.z;
    const be = b._matrix.elements;
    for (let c = 0; c < 8; c++) {
      const sx = (c & 1) ? hx : -hx;
      const sy = (c & 2) ? hy : -hy;
      const sz = (c & 4) ? hz : -hz;
      const wx = a.position.x + m[0] * sx + m[4] * sy + m[8] * sz;
      const wy = a.position.y + m[1] * sx + m[5] * sy + m[9] * sz;
      const wz = a.position.z + m[2] * sx + m[6] * sy + m[10] * sz;
      // local coords in B
      const dx = wx - b.position.x, dy = wy - b.position.y, dz = wz - b.position.z;
      const lx = dx * be[0] + dy * be[1] + dz * be[2];
      const ly = dx * be[4] + dy * be[5] + dz * be[6];
      const lz = dx * be[8] + dy * be[9] + dz * be[10];
      const ox = b.half.x - Math.abs(lx);
      const oy = b.half.y - Math.abs(ly);
      const oz = b.half.z - Math.abs(lz);
      if (ox < -SPECULATIVE || oy < -SPECULATIVE || oz < -SPECULATIVE) continue;
      // minimum translation axis
      let axis = 0, depth = ox;
      if (oy < depth) { axis = 1; depth = oy; }
      if (oz < depth) { axis = 2; depth = oz; }
      const s = axis === 0 ? Math.sign(lx) || 1 : axis === 1 ? Math.sign(ly) || 1 : Math.sign(lz) || 1;
      const nx = be[axis * 4] * s, ny = be[axis * 4 + 1] * s, nz = be[axis * 4 + 2] * s;
      const sep = -depth;
      if (flip) this._pushContact(b, a, wx, wy, wz, -nx, -ny, -nz, sep, 0);
      else this._pushContact(a, b, wx, wy, wz, nx, ny, nz, sep, 0);
    }
  }

  _pushContact(a, b, px, py, pz, nx, ny, nz, sep, surf) {
    if (b) {
      // Wake first (a moving neighbour must be able to disturb a sleeper),
      // THEN treat any body still sleeping as static. `wasMoving` is measured
      // post-solve: at contact time every awake body already carries one step
      // of gravity (0.18 m/s), which would look like motion and wake the whole
      // pile every frame.
      if (b.sleeping && !a.sleeping && a.wasMoving) b.wake();
      else if (a.sleeping && !b.sleeping && b.wasMoving) a.wake();
      // A sleeping body is immovable this step, so solve against it as if it
      // were static. Otherwise the solver keeps pushing velocity into a body
      // that never integrates, and that stale velocity corrupts the contact
      // solve for whatever is resting on top of it — the pile never settles.
      if (a.sleeping && !b.sleeping) {
        const t = a; a = b; b = t;
        nx = -nx; ny = -ny; nz = -nz;
      }
      if (b.sleeping) b = null;
    }
    let c = this.contacts[this.contactCount];
    if (!c) { c = new Contact(); this.contacts[this.contactCount] = c; }
    this.contactCount++;
    c.a = a; c.b = b;
    c.px = px; c.py = py; c.pz = pz;
    c.nx = nx; c.ny = ny; c.nz = nz;
    c.separation = sep;
    c.surface = surf;
    if (sep < SPECULATIVE * 0.5) { a.touchCount++; if (b) b.touchCount++; }
    c.normalImpulse = 0; c.tangentImpulse1 = 0; c.tangentImpulse2 = 0;
    const bR = b ? b.restitution : 0.2;
    const bF = b ? b.friction : 0.85;
    c.restitution = Math.max(a.restitution, bR);
    c.friction = Math.sqrt(a.friction * bF);
  }

  _prepare(dt) {
    const invDt = 1 / dt;
    for (let i = 0; i < this.contactCount; i++) {
      const c = this.contacts[i];
      const a = c.a, b = c.b;
      c.rax = c.px - a.position.x; c.ray = c.py - a.position.y; c.raz = c.pz - a.position.z;
      if (b) { c.rbx = c.px - b.position.x; c.rby = c.py - b.position.y; c.rbz = c.pz - b.position.z; }

      // tangents
      let t1x, t1y, t1z;
      if (Math.abs(c.nx) > 0.57735) { t1x = c.ny; t1y = -c.nx; t1z = 0; }
      else { t1x = 0; t1y = c.nz; t1z = -c.ny; }
      const tl = Math.hypot(t1x, t1y, t1z) || 1;
      t1x /= tl; t1y /= tl; t1z /= tl;
      c.t1x = t1x; c.t1y = t1y; c.t1z = t1z;
      c.t2x = c.ny * t1z - c.nz * t1y;
      c.t2y = c.nz * t1x - c.nx * t1z;
      c.t2z = c.nx * t1y - c.ny * t1x;

      c.normalMass = 1 / effectiveMass(a, b, c.rax, c.ray, c.raz, c.rbx, c.rby, c.rbz, c.nx, c.ny, c.nz);
      c.tMass1 = 1 / effectiveMass(a, b, c.rax, c.ray, c.raz, c.rbx, c.rby, c.rbz, c.t1x, c.t1y, c.t1z);
      c.tMass2 = 1 / effectiveMass(a, b, c.rax, c.ray, c.raz, c.rbx, c.rby, c.rbz, c.t2x, c.t2y, c.t2z);

      // Real-velocity bias: speculative contacts only (this is a hard
      // non-penetration constraint, not a correction — it must not be split).
      c.velocityBias = c.separation > 0 ? c.separation * invDt : 0;
      // restitution only for genuine impacts
      const vn = relativeNormalVelocity(c, false);
      c.velocityBias += (vn < -REST_THRESHOLD) ? c.restitution * vn : 0;

      // Pseudo-velocity bias: Baumgarte positional correction.
      c.positionBias = c.separation < -SLOP
        ? Math.max(BAUMGARTE * (c.separation + SLOP) * invDt, -MAX_CORRECTION)
        : 0;
      c.pseudoImpulse = 0;
    }
  }

  _solveVelocity() {
    for (let i = 0; i < this.contactCount; i++) {
      const c = this.contacts[i];
      const a = c.a, b = c.b;

      // normal
      const vn = relativeNormalVelocity(c, false);
      let lambda = -c.normalMass * (vn + c.velocityBias);
      const oldN = c.normalImpulse;
      c.normalImpulse = Math.max(0, oldN + lambda);
      lambda = c.normalImpulse - oldN;
      applyContactImpulse(c, a, b, c.nx * lambda, c.ny * lambda, c.nz * lambda, false);

      // friction (Coulomb cone against the accumulated normal impulse)
      const maxF = c.friction * c.normalImpulse;
      let vt = relativeVelocityAlong(c, c.t1x, c.t1y, c.t1z, false);
      let lt = -c.tMass1 * vt;
      let old = c.tangentImpulse1;
      c.tangentImpulse1 = clamp(old + lt, -maxF, maxF);
      lt = c.tangentImpulse1 - old;
      applyContactImpulse(c, a, b, c.t1x * lt, c.t1y * lt, c.t1z * lt, false);

      vt = relativeVelocityAlong(c, c.t2x, c.t2y, c.t2z, false);
      lt = -c.tMass2 * vt;
      old = c.tangentImpulse2;
      c.tangentImpulse2 = clamp(old + lt, -maxF, maxF);
      lt = c.tangentImpulse2 - old;
      applyContactImpulse(c, a, b, c.t2x * lt, c.t2y * lt, c.t2z * lt, false);
    }
  }

  /**
   * Split impulse: the same normal constraint solved on a separate pseudo
   * velocity that is used for integration only and then discarded. Penetration
   * is removed without adding kinetic energy, which is what stops a settled
   * pile of debris from buzzing.
   */
  _solvePosition() {
    for (let i = 0; i < this.contactCount; i++) {
      const c = this.contacts[i];
      if (c.positionBias === 0) continue;
      const a = c.a, b = c.b;
      const vn = relativeNormalVelocity(c, true);
      let lambda = -c.normalMass * (vn + c.positionBias);
      const old = c.pseudoImpulse;
      c.pseudoImpulse = Math.max(0, old + lambda);
      lambda = c.pseudoImpulse - old;
      applyContactImpulse(c, a, b, c.nx * lambda, c.ny * lambda, c.nz * lambda, true);
    }
  }

  /* ---- explosions ---- */
  applyRadialImpulse(center, radius, power) {
    const q = this.queries;
    let affected = 0;
    for (const b of this.bodies) {
      if (b.invMass === 0) continue;
      _tmpV.subVectors(b.position, center);
      const d = _tmpV.length();
      if (d > radius) continue;
      // line-of-sight occlusion: a wall between the blast and the debris kills it
      if (d > 0.05 && q.bvh.triCount > 0) {
        _tmpV2.copy(_tmpV).multiplyScalar(1 / d);
        const hit = q.raycast(center, _tmpV2, d - Math.min(0.15, b.maxHalf), this._staticMask, _losResult);
        if (hit) continue;
      }
      const falloff = 1 - d / radius;
      const mag = power * falloff * falloff;
      if (d > 1e-4) _tmpV.multiplyScalar(1 / d); else _tmpV.set(0, 1, 0);
      _tmpV.y += 0.35;             // a bit of lift reads better
      _tmpV.normalize();
      b.wake();
      // deterministic torque offset from the seeded RNG
      const jx = (this.rng() - 0.5) * b.maxHalf;
      const jy = (this.rng() - 0.5) * b.maxHalf;
      const jz = (this.rng() - 0.5) * b.maxHalf;
      b.applyImpulse(
        _tmpV.x * mag, _tmpV.y * mag, _tmpV.z * mag,
        b.position.x + jx, b.position.y + jy, b.position.z + jz,
      );
      affected++;
    }
    return affected;
  }

  /* ---- rendering ---- */
  syncTransform(b) {
    if (b.mesh) {
      b.mesh.position.copy(b.position);
      b.mesh.quaternion.copy(b.quaternion);
      b.mesh.updateMatrix();
    } else if (b.instance >= 0) {
      const mesh = b.instanceKind === 'sphere' ? this.debrisSphereMesh : this.debrisMesh;
      if (b.shape === 'sphere') _scale.setScalar(b.radius * 2);
      else _scale.set(b.half.x * 2, b.half.y * 2, b.half.z * 2);
      _mat.compose(b.position, b.quaternion, _scale);
      mesh.setMatrixAt(b.instance, _mat);
      mesh.instanceMatrix.needsUpdate = true;
    }
  }

  syncAll() {
    for (const b of this.bodies) this.syncTransform(b);
    if (this.debrisMesh) this.debrisMesh.instanceMatrix.needsUpdate = true;
    if (this.debrisSphereMesh) this.debrisSphereMesh.instanceMatrix.needsUpdate = true;
  }
}

/* ------------------------------------------------------------------ *
 * helpers
 * ------------------------------------------------------------------ */

function applyInvInertia(b, tx, ty, tz, out) {
  // world -> local, scale by the diagonal inverse inertia, local -> world
  _tmpV2.set(tx, ty, tz).applyQuaternion(_conj.copy(b.quaternion).conjugate());
  _tmpV2.x *= b.invInertia.x; _tmpV2.y *= b.invInertia.y; _tmpV2.z *= b.invInertia.z;
  out.copy(_tmpV2).applyQuaternion(b.quaternion);
  return out;
}

function effectiveMass(a, b, rax, ray, raz, rbx, rby, rbz, nx, ny, nz) {
  let k = a.invMass + (b ? b.invMass : 0);
  // a: (I^-1 (ra x n)) x ra . n
  let cx = ray * nz - raz * ny, cy = raz * nx - rax * nz, cz = rax * ny - ray * nx;
  applyInvInertia(a, cx, cy, cz, _tmpV3);
  k += (_tmpV3.y * raz - _tmpV3.z * ray) * nx + (_tmpV3.z * rax - _tmpV3.x * raz) * ny + (_tmpV3.x * ray - _tmpV3.y * rax) * nz;
  if (b) {
    cx = rby * nz - rbz * ny; cy = rbz * nx - rbx * nz; cz = rbx * ny - rby * nx;
    applyInvInertia(b, cx, cy, cz, _tmpV3);
    k += (_tmpV3.y * rbz - _tmpV3.z * rby) * nx + (_tmpV3.z * rbx - _tmpV3.x * rbz) * ny + (_tmpV3.x * rby - _tmpV3.y * rbx) * nz;
  }
  return Math.max(k, 1e-9);
}

function relativeNormalVelocity(c, pseudo) {
  return relativeVelocityAlong(c, c.nx, c.ny, c.nz, pseudo);
}

function relativeVelocityAlong(c, ax, ay, az, pseudo) {
  const a = c.a, b = c.b;
  const av = pseudo ? a.pseudoVel : a.velocity;
  const aw = pseudo ? a.pseudoAng : a.angularVelocity;
  let vx = av.x + (aw.y * c.raz - aw.z * c.ray);
  let vy = av.y + (aw.z * c.rax - aw.x * c.raz);
  let vz = av.z + (aw.x * c.ray - aw.y * c.rax);
  if (b) {
    const bv = pseudo ? b.pseudoVel : b.velocity;
    const bw = pseudo ? b.pseudoAng : b.angularVelocity;
    vx -= bv.x + (bw.y * c.rbz - bw.z * c.rby);
    vy -= bv.y + (bw.z * c.rbx - bw.x * c.rbz);
    vz -= bv.z + (bw.x * c.rby - bw.y * c.rbx);
  }
  return vx * ax + vy * ay + vz * az;
}

function applyContactImpulse(c, a, b, ix, iy, iz, pseudo) {
  if (a.invMass > 0) {
    const av = pseudo ? a.pseudoVel : a.velocity;
    const aw = pseudo ? a.pseudoAng : a.angularVelocity;
    av.x += ix * a.invMass; av.y += iy * a.invMass; av.z += iz * a.invMass;
    const tx = c.ray * iz - c.raz * iy, ty = c.raz * ix - c.rax * iz, tz = c.rax * iy - c.ray * ix;
    applyInvInertia(a, tx, ty, tz, _tmpV4);
    aw.add(_tmpV4);
  }
  if (b && b.invMass > 0) {
    const bv = pseudo ? b.pseudoVel : b.velocity;
    const bw = pseudo ? b.pseudoAng : b.angularVelocity;
    bv.x -= ix * b.invMass; bv.y -= iy * b.invMass; bv.z -= iz * b.invMass;
    const tx = c.rby * iz - c.rbz * iy, ty = c.rbz * ix - c.rbx * iz, tz = c.rbx * iy - c.rby * ix;
    applyInvInertia(b, tx, ty, tz, _tmpV4);
    bw.sub(_tmpV4);
  }
}

function _hideAll(mesh, cap) {
  _mat.makeScale(0, 0, 0);
  for (let i = 0; i < cap; i++) mesh.setMatrixAt(i, _mat);
  mesh.instanceMatrix.needsUpdate = true;
}
function _hideInstance(mesh, i) {
  if (!mesh) return;
  _mat.makeScale(0, 0, 0);
  mesh.setMatrixAt(i, _mat);
  mesh.instanceMatrix.needsUpdate = true;
}

const _now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const _zero = new THREE.Vector3();
const _one = new THREE.Vector3(1, 1, 1);
const _identQ = new THREE.Quaternion();
const _tmpV = new THREE.Vector3();
const _tmpV2 = new THREE.Vector3();
const _tmpV3 = new THREE.Vector3();
const _tmpV4 = new THREE.Vector3();
const _tmpQ = new THREE.Quaternion();
const _conj = new THREE.Quaternion();
const _mat = new THREE.Matrix4();
const _scale = new THREE.Vector3();
const _step = new THREE.Vector3();
const _ccd = { t: 0, tri: -1, nx: 0, ny: 0, nz: 0, px: 0, py: 0, pz: 0, depth: 0 };
const _cp = { x: 0, y: 0, z: 0 };
const _losResult = {
  point: new THREE.Vector3(), normal: new THREE.Vector3(),
  distance: 0, object: null, surface: 'concrete', triangle: -1, body: null,
};
